import { apiSlice } from './apiSlice'
import { TRAINING_PACKAGES } from './tags'

export const trainingTopicsAPI = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getTrainingTopics: builder.query({
      query: ({ language, type, searchText }) => ({
        url: `/v1/trainings`,
        method: 'GET',
        params: {
          language,
          type,
          searchText
        }
      }),
      providesTags: [TRAINING_PACKAGES.TOPICS]
    }),
    addTrainingTopic: builder.mutation({
      query: body => {
        return {
          url: `/v1/trainings`,
          method: 'POST',
          body
        }
      },
      invalidatesTags: [TRAINING_PACKAGES.TOPICS]
    }),
    updateTrainingTopic: builder.mutation({
      query: ({ id, ...body }) => {
        return {
          url: `/v1/trainings/${id}`,
          method: 'PUT',
          body
        }
      },
      invalidatesTags: [TRAINING_PACKAGES.TOPICS]
    }),
    getTrainingModules: builder.query({
      query: ({ topicId, language }) => ({
        url: `/v1/trainings/${topicId}/internal-modules`,
        method: 'GET',
        params: { language }
      }),
      providesTags: (result, error, { topicId }) => [{ type: TRAINING_PACKAGES.MODULES, id: topicId }]
    }),
    getModulePreview: builder.query({
      query: ({ topicId, moduleId, language }) => ({
        url: `/v1/trainings/${topicId}/internal-modules/${moduleId}/`,
        method: 'GET',
        params: { language }
      })
    }),
    toggleTrainingActivation: builder.mutation({
      query: ({ id, active }) => ({
        url: `/v1/trainings/${id}/activate`,
        method: 'POST',
        data: { active }
      }),
      invalidatesTags: [TRAINING_PACKAGES.TOPICS]
    }),
    deleteTrainingTopic: builder.mutation({
      query: ({ id }) => ({
        url: `/v1/trainings/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TRAINING_PACKAGES.TOPICS]
    }),
    getCustomModules: builder.query({
      query: ({ topicId, language }) => ({
        url: `/v1/trainings/${topicId}/custom-modules`,
        method: 'GET',
        params: { language }
      }),
      providesTags: (result, error, { topicId }) => [{ type: TRAINING_PACKAGES.CUSTOM_MODULES, id: topicId }]
    }),
    getCustomModule: builder.query({
      query: ({ topicId, moduleId, language }) => ({
        url: `/v1/trainings/${topicId}/custom-modules/${moduleId}`,
        method: 'GET',
        params: { language }
      })
    }),
    addCustomModule: builder.mutation({
      query: ({ topicId, ...body }) => ({
        url: `/v1/trainings/${topicId}/custom-modules`,
        method: 'POST',
        body
      }),
      invalidatesTags: (result, error, { topicId }) => [
        { type: TRAINING_PACKAGES.CUSTOM_MODULES, id: topicId },
        { type: TRAINING_PACKAGES.TOPICS }
      ]
    }),
    updateCustomModule: builder.mutation({
      query: ({ topicId, moduleId, ...body }) => ({
        url: `/v1/trainings/${topicId}/custom-modules/${moduleId}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (result, error, { topicId }) => [
        { type: TRAINING_PACKAGES.CUSTOM_MODULES, id: topicId },
        { type: TRAINING_PACKAGES.TOPICS }
      ]
    }),
    addAiModule: builder.mutation({
      query: ({ topicId, ...body }) => ({
        url: `/v1/trainings/${topicId}/ai-custom-modules`,
        method: 'POST',
        body
      }),
      invalidatesTags: (result, error, { topicId }) => [
        { type: TRAINING_PACKAGES.CUSTOM_MODULES, id: topicId },
        { type: TRAINING_PACKAGES.TOPICS }
      ]
    }),
    updateInternalModule: builder.mutation({
      query: ({ topicId, language, moduleId, ...body }) => ({
        url: `/v1/trainings/${topicId}/internal-modules/${moduleId}`,
        method: 'PUT',
        params: { language },
        body
      }),
      invalidatesTags: (result, error, { topicId }) => [
        { type: TRAINING_PACKAGES.MODULES, id: topicId },
        { type: TRAINING_PACKAGES.TOPICS }
      ]
    }),
    renameCustomModule: builder.mutation({
      query: ({ topicId, moduleId, ...body }) => ({
        url: `/v1/trainings/${topicId}/custom-modules/${moduleId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (result, error, { topicId }) => [
        { type: TRAINING_PACKAGES.CUSTOM_MODULES, id: topicId },
        { type: TRAINING_PACKAGES.TOPICS }
      ]
    }),
    deleteCustomModule: builder.mutation({
      query: ({ topicId, moduleId }) => ({
        url: `/v1/trainings/${topicId}/custom-modules/${moduleId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, { topicId }) => [
        { type: TRAINING_PACKAGES.CUSTOM_MODULES, id: topicId },
        { type: TRAINING_PACKAGES.TOPICS }
      ]
    }),
    uploadImage: builder.mutation({
      query: ({ topicId, body }) => ({
        url: `/v1/trainings/${topicId}/images`,
        method: 'POST',
        body
      })
    }),
    uploadVideo: builder.mutation({
      query: ({ topicId, body }) => ({
        url: `/v1/trainings/${topicId}/videos`,
        method: 'POST',
        body
      })
    })
  })
})

export const {
  useUploadVideoMutation,
  useRenameCustomModuleMutation,
  useGetTrainingTopicsQuery,
  useUpdateTrainingTopicMutation,
  useToggleTrainingActivationMutation,
  useAddTrainingTopicMutation,
  useDeleteTrainingTopicMutation,
  useGetCustomModuleQuery,
  useLazyGetCustomModuleQuery,
  useLazyGetModulePreviewQuery,
  useGetModulePreviewQuery,
  useLazyGetTrainingModulesQuery,
  useLazyGetCustomModulesQuery,
  useAddCustomModuleMutation,
  useAddAiModuleMutation,
  useUpdateCustomModuleMutation,
  useUpdateInternalModuleMutation,
  useDeleteCustomModuleMutation,
  useUploadImageMutation
} = trainingTopicsAPI
